import { useState, useEffect } from 'react';

interface WelcomeGuideProps {
  chatbotName: string;
  emailTabVisible: boolean;
  chatbotTabVisible: boolean;
  onClose: () => void;
}

export default function WelcomeGuide({ chatbotName, emailTabVisible, chatbotTabVisible, onClose }: WelcomeGuideProps) {
  const [step, setStep] = useState(0);
  const totalSteps = 3;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="modal-overlay" style={{ padding: 'var(--space-4)' }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="welcome-guide">
        {step === 0 && (
          <>
            <div className="welcome-guide-step-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h2>Welcome to Your AI Assistant</h2>
            <p>
              This is your control panel for <strong>{chatbotName}</strong>. From here, you can manage an AI-powered chatbot
              {emailTabVisible ? ' and email responder' : ''} that helps answer your customers' questions automatically.
            </p>
          </>
        )}

        {step === 1 && (
          <>
            <div className="welcome-guide-step-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
            </div>
            <h2>What You Can Do Here</h2>
            <div className="welcome-guide-features">
              {chatbotTabVisible && (
                <div className="welcome-guide-feature">
                  <div className="welcome-guide-feature-icon" style={{ background: 'var(--color-primary-light)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4>Chatbot</h4>
                    <p>Customize how your chat widget looks and behaves on your website.</p>
                  </div>
                </div>
              )}
              <div className="welcome-guide-feature">
                <div className="welcome-guide-feature-icon" style={{ background: 'var(--color-success-light)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                </div>
                <div>
                  <h4>Knowledge Base</h4>
                  <p>Tell the AI about your business so it can answer questions accurately.</p>
                </div>
              </div>
              {emailTabVisible && (
                <div className="welcome-guide-feature">
                  <div className="welcome-guide-feature-icon" style={{ background: 'var(--color-warning-light)' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div>
                    <h4>Email Responder</h4>
                    <p>Let the AI draft replies to incoming emails for you to review and send.</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="welcome-guide-step-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
              </svg>
            </div>
            <h2>Getting Started</h2>
            <p>
              Follow the setup steps at the top of the page to get your AI assistant ready.
              Start by describing your business, then add documents or FAQs to build your knowledge base.
            </p>
          </>
        )}

        <div className="welcome-guide-nav">
          {step > 0 ? (
            <button className="btn btn-ghost" onClick={() => setStep(step - 1)}>Back</button>
          ) : (
            <button className="btn btn-ghost" onClick={onClose}>Skip</button>
          )}

          <div className="welcome-guide-dots">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div key={i} className={`welcome-guide-dot ${i === step ? 'active' : ''}`} />
            ))}
          </div>

          {step < totalSteps - 1 ? (
            <button className="btn btn-primary" onClick={() => setStep(step + 1)}>Next</button>
          ) : (
            <button className="btn btn-primary" onClick={onClose}>Get Started</button>
          )}
        </div>
      </div>
    </div>
  );
}
